/* =========================================================
   AUTOCLINIC MAIN CAR SELECTOR SCRIPT
   Reorganized with section headers only.
   Existing functions and execution order are preserved.
========================================================= */

/* =========================================================
   01. CONFIG / GLOBAL VARIABLES
========================================================= */

var akrapovicCsvUrl = 'https://autoclinic.co.th/database/akra-price-th-04-08-26.csv';
var akrapovicCollectionUrl = 'https://autoclinic.com/th-th/collections/exhaust';

let cachedCarDb = null;
let carDbRequest = null;
let indexedCarDb = null;
let indexedById = {};
const CAR_DB_CACHE_KEY = 'autoclinic_akrapovic_car_db_cache_v3';
const CAR_DB_CACHE_TTL = 24 * 60 * 60 * 1000;

// === Normalize helper ===
const normalize = (val) => String(val || '').trim().toLowerCase();

// === Indexing function ===


/* =========================================================
   02. DATABASE FETCHING / INDEXING
========================================================= */

function indexCarDb(carDb) {
    const index = {};

    carDb.forEach(car => {
        const make = normalize(car.make);
        const model = normalize(car.model);
        const carName = normalize(car.carName);
        const power = normalize(car['power(kW)']);
        const cyl = normalize(car.cyl);
        const drivetype = normalize(car.drivetype);
        const carId = car.carId;

        const key = `${carName}--${power}--${cyl}--${drivetype}`;

        if (!index[make]) index[make] = {};
        if (!index[make][model]) index[make][model] = {};
        if (!index[make][model][key]) index[make][model][key] = [];

        index[make][model][key].push(car);

        if (carId) {
            indexedById[carId] = car;
        }
    });

    return index;
}

let brandDb = null;
let exhaustDb = null;

function parseAkrapovicCSV(text) {
   const rows = [];
   let row = [];
   let field = '';
   let inQuotes = false;

   for (let i = 0; i < text.length; i++) {
      const char = text[i];

      if (inQuotes) {
         if (char === '"') {
            if (text[i + 1] === '"') {
               field += '"';
               i++;
            } else {
               inQuotes = false;
            }
         } else {
            field += char;
         }
      } else if (char === '"') {
         inQuotes = true;
      } else if (char === ',') {
         row.push(field);
         field = '';
      } else if (char === '\n') {
         row.push(field);
         rows.push(row);
         row = [];
         field = '';
      } else if (char !== '\r') {
         field += char;
      }
   }

   if (field.length || row.length) {
      row.push(field);
      rows.push(row);
   }

   return rows;
}

function normalizeAkrapovicCsvRows(rows) {
   if (!rows.length) return [];

   const headers = rows[0].map(header => String(header || '').trim().replace(/^\uFEFF/, ''));
   const normalizeHeader = header => String(header || '').trim().toLowerCase().replace(/[\s_-]/g, '');
   const normalizedHeaders = headers.map(normalizeHeader);
   const indexOf = name => normalizedHeaders.indexOf(normalizeHeader(name));
   const firstExistingIndex = names => {
      for (const name of names) {
         const index = indexOf(name);
         if (index !== -1) return index;
      }

      return -1;
   };

   const salesIdIndex = firstExistingIndex(['Sales ID', 'salesId', 'SKU']);
   const carIdIndex = firstExistingIndex(['carID', 'carId', 'Car ID', 'CarID', 'car_id', 'ID']);
   const brandIndex = indexOf('Brand');
   const modelIndex = indexOf('Model');
   const fromIndex = indexOf('MY from');
   const toIndex = indexOf('MY to');
   const commercialNameIndex = indexOf('Commercial Name');
   const lineIndex = indexOf('Exhaust System Line');
   const columnValue = (columns, index) => index === -1 ? '' : String(columns[index] || '').trim();

   return rows.slice(1).flatMap(columns => {
      const firstColumn = columnValue(columns, 0);
      const firstColumnCarId = /^\d+$/.test(firstColumn) ? firstColumn : '';
      const salesId = columnValue(columns, salesIdIndex !== -1 ? salesIdIndex : (firstColumnCarId ? 1 : 0));
      const rawCarId = columnValue(columns, carIdIndex);
      const carId = /^\d+$/.test(rawCarId) ? rawCarId : firstColumnCarId;
      const brand = columnValue(columns, brandIndex);
      const model = columnValue(columns, modelIndex);
      const yearFrom = Number(columnValue(columns, fromIndex));
      const yearTo = Number(columnValue(columns, toIndex));
      const commercialName = columnValue(columns, commercialNameIndex);
      const exhaustLine = columnValue(columns, lineIndex);

      if (!salesId || !brand || !model || !Number.isFinite(yearFrom) || !Number.isFinite(yearTo)) {
         return [];
      }

      const years = [];
      for (let year = yearFrom; year <= yearTo; year++) {
         years.push({
            make: brand,
            brand,
            model,
            carName: String(year),
            'power(kW)': '',
            ccm: '',
            cyl: '',
            drivetype: '',
            chassis: '',
            aspiration: '',
            carId,
            salesId,
            akrapovic: salesId,
            'racingSystem (noCertification)': salesId,
            commercialName,
            exhaustLine,
            _source: 'akrapovicCsv'
         });
      }

      return years;
   });
}

function readCachedCarDb() {
   try {
      const cached = JSON.parse(localStorage.getItem(CAR_DB_CACHE_KEY) || 'null');

      if (
         cached &&
         cached.url === akrapovicCsvUrl &&
         Array.isArray(cached.data) &&
         Date.now() - cached.savedAt < CAR_DB_CACHE_TTL
      ) {
         return cached.data;
      }
   } catch (error) {}

   return null;
}

function writeCachedCarDb(carDb) {
   try {
      localStorage.setItem(CAR_DB_CACHE_KEY, JSON.stringify({
         url: akrapovicCsvUrl,
         savedAt: Date.now(),
         data: carDb
      }));
   } catch (error) {}
}

const fetchBrandrDb = () => {
   if (brandDb) return Promise.resolve(brandDb);

   return fetchCarDb().then((carDb) => {
      brandDb = [...new Set(carDb.map(item => item.make))]
         .sort((a, b) => a.localeCompare(b))
         .map(brand => ({ brand }));
      return brandDb;
   });
};

/* Start Automatic Car Selection Script */
const fetchCarDb = () => {
    if (cachedCarDb) return Promise.resolve(cachedCarDb);
    if (carDbRequest) return carDbRequest;

    const localCache = readCachedCarDb();
    if (localCache) {
        cachedCarDb = localCache;
        return Promise.resolve(cachedCarDb);
    }

    carDbRequest = fetch(akrapovicCsvUrl, { cache: 'no-store' })
        .then(response => {
            if (!response.ok) {
                throw new Error(`Could not load Akrapovič CSV (${response.status})`);
            }
            return response.text();
        })
        .then((csvText) => {
            cachedCarDb = normalizeAkrapovicCsvRows(parseAkrapovicCSV(csvText));
            writeCachedCarDb(cachedCarDb);
            return cachedCarDb;
        })
        .finally(() => {
            carDbRequest = null;
        });

    return carDbRequest;
};

// Function to check if `carId` has changed and clear selection if needed
// For page reload


/* =========================================================
   03. URL carId AUTO-SELECTION
========================================================= */

function checkCarIdChange() {
  const currentCarId = getCarIdFromURL();
  const previousCarId = localStorage.getItem("previousCarId");

  if (!currentCarId || currentCarId === previousCarId) return;

  const selectedCar = indexedById[currentCarId];
  if (!selectedCar) return;

  const engineValue = `${selectedCar.carName}--${selectedCar['power(kW)']}--${selectedCar.cyl}--${selectedCar.drivetype}`;

  setLocalStorage('brand', selectedCar.make);
  setLocalStorage('model', selectedCar.model);
  setLocalStorage('engine', engineValue);
  localStorage.setItem("previousCarId", currentCarId);

  saveCarDetails(engineValue);
}

// Function to check if `carId` is in URL and `.jsc-my-model-detail` is empty
function reloadIfModelDetailIsEmpty() {
  return false;
}

// Run only after the car database is loaded


// Function to get carId from the URL
function getCarIdFromURL() {
    var urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('carId'); // Extract ?carId=123 from URL
}

// Auto-Select Car from Database Based on carId in URL
function autoSelectCarFromURL() {
  var carId = getCarIdFromURL();
  if (!carId) return;

  var selectedCar = indexedById[carId];
  if (!selectedCar) return;

  var engineValue = `${selectedCar.carName}--${selectedCar['power(kW)']}--${selectedCar.cyl}--${selectedCar.drivetype}`;

  setLocalStorage('brand', selectedCar.make);
  setLocalStorage('model', selectedCar.model);
  setLocalStorage('engine', engineValue);
  localStorage.setItem("previousCarId", carId);

  saveCarDetails(engineValue);
  updateFitmentDisplay(selectedCar);

  refreshSelectedCarUIFromStorage();
}

// Function to update the Fitment Display
function updateFitmentDisplay(selectedCar) {
    var fitmentBox = $(".fitment-confirmation-box");
    if (fitmentBox.length) {
        fitmentBox.html(`
            <span class="fitment-text">
                Fitment confirmed for <strong>${selectedCar.make} ${selectedCar.model}</strong> 
                ${selectedCar.carName} - ${selectedCar['power(kW)']}kW
            </span>
        `);
        fitmentBox.show();
    }
}

// Helper Function to Convert Text into Shopify-Compatible Format


/* =========================================================
   04. LOCAL STORAGE / BASIC HELPERS
========================================================= */

function handleizedString(string) {
    return string.replace(/\s+/g, '-')
        .replace(/\//g, '-')
        .trim()
        .toLowerCase()
        .replace(/\(|\)/g, '-')
        .replace(/--/g, '-')
        .replace('.', '-');
}

// Function to Save Values in Local Storage
function setLocalStorage(key, value) {
    localStorage.setItem(key, value);
}

// Function to Get Values from Local Storage
function getLocalStorage(key) {
    return localStorage.getItem(key) || "";
}

function isThaiMarketPath() {
    return /^\/th-th(?:\/|$)/.test(window.location.pathname);
}

function isExhaustFitmentContext() {
    var path = window.location.pathname;

    return /\/collections\/exhaust(?:\/|$)/.test(path) ||
        Boolean(document.querySelector('.compatible-part, .exhaust-error, .jsc-positive-compatibility, .jsc-negative-compatibility'));
}

function shouldRemoveThaiExhaustFitment() {
    return isThaiMarketPath() && isExhaustFitmentContext();
}

function clearSelectedCarLineItemProperties() {
    $('#jsc-selected-brand-model, .jsc-selected-brand-model, #jsc-selected-engine, .jsc-selected-engine')
        .val('')
        .prop('disabled', true)
        .removeAttr('name');
    $('.jsc-selected-model-title').text('');
}

function applyThaiExhaustFitmentRules() {
    if (!shouldRemoveThaiExhaustFitment()) return;

    $('.fit-wrap').hide();
    renderSelectedCarDetails();
    clearSelectedCarLineItemProperties();
}

function renameEngineSelectorToYear() {
   $('#jsc-engine-label').text('Year');
   $('#jsc-engine-label, .jsc-engine .label').text('Year');
   $('.search-engine').attr('placeholder', 'Search Year');

   const engineButtonText = $('.jsc-engine .custom-select__btn span.text-start');
   if (!getLocalStorage('engine') || /^Engine/i.test(engineButtonText.text().trim())) {
      engineButtonText.text('Year...');
   }
}

function isAkrapovicCsvCar(car) {
    return car && car._source === 'akrapovicCsv';
}

function getEngineOptionValue(car) {
    return isAkrapovicCsvCar(car)
        ? car.carName
        : [car.carName, car['power(kW)'], car.cyl, car.drivetype].join('--');
}

function getEngineOptionLabel(car) {
    return isAkrapovicCsvCar(car)
        ? car.carName
        : [car.carName, car['power(kW)'], car.cyl, car.drivetype].join('-');
}

function carMatchesSelection(car, brand, model, engineArray) {
    if (isAkrapovicCsvCar(car)) {
        return normalize(car.make) === normalize(brand) &&
            normalize(car.model) === normalize(model) &&
            normalize(car.carName) === normalize(engineArray[0]);
    }

    return normalize(car.make) === normalize(brand) &&
        normalize(car.model) === normalize(model) &&
        normalize(car.carName) === normalize(engineArray[0]) &&
        normalize(car['power(kW)']) === normalize(engineArray[1]) &&
        normalize(car.cyl) === normalize(engineArray[2]) &&
        normalize(car.drivetype) === normalize(engineArray[3]);
}

function buildAkrapovicCollectionUrl() {
    const brand = getLocalStorage('brand');
    const model = getLocalStorage('model');
    const engine = getLocalStorage('engine');

    if (!brand || !model || !engine || !cachedCarDb || !cachedCarDb.length) {
        return akrapovicCollectionUrl;
    }

    const engineArray = engine.split('--');
    const salesIds = [
        ...new Set(
            cachedCarDb
                .filter(car => carMatchesSelection(car, brand, model, engineArray))
                .map(car => car.salesId || car.akrapovic || car['racingSystem (noCertification)'])
                .filter(Boolean)
        )
    ];

    const query = salesIds
        .map(id => `filter.p.m.json.handle=${encodeURIComponent(id)}`)
        .join('&');

    return query ? `${akrapovicCollectionUrl}?${query}` : akrapovicCollectionUrl;
}

function getAkrapovicActionButton(target) {
    return target.closest('.find-parts, .product-overview-for-my-car');
}

function syncAkrapovicActionButtons() {
    const url = buildAkrapovicCollectionUrl();

    document.querySelectorAll('.find-parts, .product-overview-for-my-car').forEach(button => {
        button.setAttribute('href', url);
        button.removeAttribute('target');
    });
}

document.addEventListener('click', function (event) {
    const actionButton = getAkrapovicActionButton(event.target);
    if (!actionButton) return;

    const isDisabled = actionButton.getAttribute('aria-disabled') === 'true';
    if (isDisabled) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
    }

    const url = buildAkrapovicCollectionUrl();
    actionButton.setAttribute('href', url);
    actionButton.removeAttribute('target');

    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.href = url;
}, true);

document.addEventListener('DOMContentLoaded', function () {
    syncAkrapovicActionButtons();

    const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'target') {
                const element = mutation.target;
                if (element.matches && element.matches('.find-parts, .product-overview-for-my-car')) {
                    element.removeAttribute('target');
                }
            }
        });
    });

    document.querySelectorAll('.find-parts, .product-overview-for-my-car').forEach(button => {
        observer.observe(button, { attributes: true });
    });
});



/* =========================================================
   05. INITIAL PAGE LOAD
========================================================= */

// Run function after database loads and auto-selection completes
fetchCarDb().then(() => {
  indexedCarDb = indexCarDb(cachedCarDb);

  autoSelectCarFromURL();

  onloadPopulateCarFilterFromLocalStorage();
  onLoadCreateCompatibleTable();
  onLoadShowSpecificationContent();
  appendFilterPageLinkList();
  appendFilterPageLinks();
  filterAddOnsForSelectedCar();

  setTimeout(() => {
    refreshSelectedCarUIFromStorage();
    loader.hide();
  }, 300);
});



/* =========================================================
   06. PRODUCT LINKS WITH carId
========================================================= */

/* Dynamic carID */
document.addEventListener("DOMContentLoaded", function () {
    let updateQueued = false;

    function getProductLinks() {
        return Array.from(
            document.querySelectorAll(
                [
                    '.jsc-product-grid .card-link',
                    '.jsc-product-grid .js-prod-link',
                    '.jsc-product-grid product-card a[href*="/products/"]',
                    'li.jsc-product-grid product-card a[href*="/products/"]',
                    'product-card a[href*="/products/"]',
                    'a.js-prod-link[href*="/products/"]',
                    'a.card-link[href*="/products/"]'
                ].join(',')
            )
        ).filter(link => !link.closest('.sale-content'));
    }

    function getCarIdFromLocalStorage() {
        const brand = getLocalStorage('brand');
        const model = getLocalStorage('model');
        const engine = getLocalStorage('engine');

        if (!brand || !model || !engine || !cachedCarDb || !cachedCarDb.length) {
            return null;
        }

        const engineArray = engine.split('--');
        const selectedCar = cachedCarDb.find(car =>
            carMatchesSelection(car, brand, model, engineArray)
        );

        return selectedCar ? selectedCar.carId : null;
    }

    function appendCarIdToProductLink(link) {
        if (!link) return;

        const carId = getCarIdFromLocalStorage();
        if (!carId) return;

        const href = link.getAttribute('href');
        if (!href || !href.includes('/products/')) return;

        try {
            const url = new URL(href, window.location.origin);
            url.searchParams.set('carId', carId);

            link.setAttribute(
                'href',
                url.pathname + url.search + url.hash
            );

            link.classList.remove('disabled-link');
            link.removeAttribute('aria-disabled');
            link.style.pointerEvents = '';
            link.style.opacity = '';
        } catch (error) {
            console.warn('Unable to append carId:', href, error);
        }
    }

    function updateProductLinksWithCarId() {
        updateQueued = false;

        // Do not disable or modify links until the car database is ready.
        if (!cachedCarDb || !cachedCarDb.length) return;

        const carId = getCarIdFromLocalStorage();
        if (!carId) return;

        getProductLinks().forEach(link => {
            appendCarIdToProductLink(link);
        });
    }

    function queueProductLinkUpdate() {
        if (updateQueued) return;
        updateQueued = true;
        requestAnimationFrame(updateProductLinksWithCarId);
    }

    // Run only after the database is ready.
    fetchCarDb().then(() => {
        queueProductLinkUpdate();
        setTimeout(queueProductLinkUpdate, 300);
        setTimeout(queueProductLinkUpdate, 1000);
        setTimeout(queueProductLinkUpdate, 2000);
    });

    // Catch products added by infinite scroll or AJAX filters.
    const observer = new MutationObserver(mutations => {
        const hasAddedProducts = mutations.some(mutation =>
            Array.from(mutation.addedNodes).some(node =>
                node.nodeType === 1 &&
                (
                    node.matches?.('.js-pagination-result, product-card') ||
                    node.querySelector?.('.card-link, .js-prod-link, product-card')
                )
            )
        );

        if (hasAddedProducts) {
            queueProductLinkUpdate();
        }
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

    // Fallback after collection filtering or pagination actions.
    document.addEventListener('click', function (event) {
        if (event.target.closest('.filter-option, custom-pagination, [data-filter]')) {
            setTimeout(queueProductLinkUpdate, 150);
            setTimeout(queueProductLinkUpdate, 600);
        }
    });

    document.addEventListener('autoclinic:carIdChanged', queueProductLinkUpdate);

    document.addEventListener('mouseover', function (event) {
        appendCarIdToProductLink(event.target.closest('a[href*="/products/"]'));
    }, true);

    document.addEventListener('touchstart', function (event) {
        appendCarIdToProductLink(event.target.closest('a[href*="/products/"]'));
    }, true);

    document.addEventListener('click', function (event) {
        appendCarIdToProductLink(event.target.closest('a[href*="/products/"]'));
    }, true);

    document.addEventListener('shopify:section:load', queueProductLinkUpdate);
    document.addEventListener('shopify:section:select', queueProductLinkUpdate);
});
/* End */



var loader = $('.jsc-loader-overlay');

var blankLi = `<li class="custom-select__option flex items-center js-option" id="jsc-brand-blank" role="option" data-value="Loading..."><span class="pointer-events-none">Loading...</span></li>`;

const carDbColumn = ['coilovers', 'heightAdjustableSprings', 'loweringSprings', 'electronicCancellerKit', 'undercarriageArms', 'sportsDiscAndPads', 'brakeSystems', 'airFilter (dropIn)', 'airFilter (openPodSystem)', 'ecuPiggyback (euroCars)', 'ecuPiggyback2 (euroCars)', 'ecuPiggyback (nonEuro)', 'throttleController', 'ecuTuning', 'blowOff /DiverterValves', 'cooling', 'sparkPlug /IgnitionCoil', 'fuel', 'turbo /Supercharger', '1PieceWheel', '2PieceWheel', 'roadCompliantSystem (withCertification)', 'racingSystem (noCertification)', 'autoclinic', 'akrapovic', 'apr', 'dte', 'fox', 'gfb', 'kw', 'st', 'remus', 'completeCarTuningProgram']; // Add more link types here



/* =========================================================
   07. MAIN DOCUMENT READY INITIALIZATION
========================================================= */

$(function () {
   renameEngineSelectorToYear();
   initLoader();
   onLoadFunctionInit();
   if (location.pathname.includes('/products')) {
      addOnsCheck(exhaustDb);
      calculateTotalAmount();
   }
   fetchBrandrDb().then(() => {
      loadBrandOption();
      if (location.pathname == '/') {
         loader.hide();
      }
   }); 

   // 👉 Add here (this runs after brand/model lists are loaded)
   document.querySelectorAll(".search-brand, .search-model, .search-engine").forEach(input => {
      const parent = input.closest(".custom-select, .jsc-brand, .jsc-model, .jsc-engine");
      if (!parent) return;
      const dropdown = parent.querySelector(".custom-select__listbox");
      if (!dropdown) return;

      let debounceTimer;
      input.addEventListener("input", () => {
         clearTimeout(debounceTimer);
         debounceTimer = setTimeout(() => {
         const query = input.value.trim().toLowerCase();
         const options = dropdown.querySelectorAll(".custom-select__option");
         if (!options.length) return;

         let matchFound = false;
         options.forEach(option => {
            const match = option.textContent.toLowerCase().includes(query);
            option.style.display = match || query === "" ? "" : "none";
            if (match) matchFound = true;
         });

         dropdown.hidden = !matchFound && query !== "";
         }, 100);
      });
   });
   fetchCarDb().then(() => {
      onloadPopulateCarFilterFromLocalStorage();
      onLoadCreateCompatibleTable();
      onLoadShowSpecificationContent();
      appendFilterPageLinkList();
      checkCompatibility();
      renameEngineSelectorToYear();
      applyThaiExhaustFitmentRules();
      loader.hide();
      appendFilterPageLinks();
   });
   initMultiColumnSwiper();
});

onLoadFunctionInit = () =>{
   renameEngineSelectorToYear();
   loadMyCarModel();
   loadHeaderCarInfo();
   appendURLFilterParamAsHiddenInputInCPFilter();
   hideCPFilterOption();
   hideCpFilterWhenEmpty();
   loadCarInfoToLIProperties();
   applyThaiExhaustFitmentRules();
   selectAndStoreRandomGridElements();
   loadUpsellGrid();
   onloadDateRange();
}

selectAndStoreRandomGridElements = () => {
   // Step 1: Select all elements with the class 'jsc-product-grid'
   var elements = $('.jsc-product-grid').toArray();

   if (elements.length > 1) {
      // Step 2: Shuffle the array
      function shuffle(array) {
         for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
         }
         return array;
      }

      var shuffledElements = shuffle(elements);

      // Step 3: Pick the first three elements
      var selectedElements = shuffledElements.slice(0, 3);

      // Step 4: Store their indices in local storage
      var selectedIndices = selectedElements.map(function(elem) {
         return elem.outerHTML;
      });

      localStorage.setItem('selectedProducts', JSON.stringify(selectedIndices));
      localStorage.setItem('upsellCollectionSelected', $('#jsc-collection-handle').val());
   }
}

loadUpsellGrid = () =>{
   var gridHTML = getLocalStorage('selectedProducts');
   var upsellCollection = getLocalStorage('upsellCollectionSelected');
   var productCollectionHandles = $('#jsc-product-colecttion-handles').val();

   if (productCollectionHandles) {
      if (gridHTML && productCollectionHandles.includes(upsellCollection)) {
         var currentProductHandle = $('#jsc-product-handles').val();
         var htmlArray = JSON.parse(gridHTML);
         var count = 0;
         htmlArray.forEach(element => {
            var _thisProductHandle = $(element).attr('data-handle');
            if (currentProductHandle != _thisProductHandle && count < 2) {
               $('jsc-upssell ul').append(element);
               count++;
            }
         });
         $('jsc-upssell .jsc-section-title').show();
      }
   }
}

initLoader = () =>{
   var getBrandLocalStorage = getLocalStorage('brand');
   var getModelLocalStorage = getLocalStorage('model');
   var getEngineLocalStorage = getLocalStorage('engine');
   if (getBrandLocalStorage == '' || getModelLocalStorage == '' || getEngineLocalStorage == '')  {
      loader.show();
   } else if ($('#jsc-template').val() == 'page.filter') {
      loader.show();
   }
}

handleizedString = (string) => {
   return string.replace(/\s+/g, '-').replace(/\//g, '-').trim().toLowerCase().replace('(', '-').replace(')', '-').replace(/--/g, '-').replace('.', '-');
}



/* =========================================================
   08. CAR SELECTOR DROPDOWN OPTIONS
========================================================= */

populateModelHtml = (jsonData, brand, localStorageModel) => {
   // Filter the JSON data for the selected "Brand"
   var filteredData = jsonData.filter(function(item) {
      return item.make === brand;
   });

   var modelUlElement = $('.jsc-model .custom-select__listbox');
   var modelOptions = [...new Set(filteredData.map(item => item.model))].sort().map(model => {
      var handleizedModel = handleizedString(model);
      return `<li class="custom-select__option text-left flex items-center js-option ${model == localStorageModel ? 'active-selected' : ''}" id="jsc-model-${handleizedModel}" role="option" data-value="${model}" onclick="selectOption(this,'model')" ${model == localStorageModel ? 'aria-selected="true"' : 'aria-selected="false"'}>
                  <span class="pointer-events-none">${model}</span>
               </li>`;
   });

   modelUlElement.removeClass('hide').attr('hidden', true).html(modelOptions.join(''));

   $('.jsc-model .custom-select__btn').addClass('jsc-enabled-dropdown');
   $('.jsc-model').find('.custom-select__btn').prop('disabled', false);
}

populateEngineHtml = (jsonData, model, localStorageEngine) => {
   // Filter the JSON data for the selected "Model"
   var selectedBrand = getLocalStorage('brand');
   var filteredData = jsonData.filter(function(item) {
      return item.model === model && (!selectedBrand || item.make === selectedBrand);
   });

   var engineUlElement = $('.jsc-engine .custom-select__listbox');

   // For Akrapovič CSV data this becomes the Year dropdown.
   var uniqueEngineOptions = new Set();
   var engineOptions = filteredData
      .map(item => ({
         value: getEngineOptionValue(item),
         label: getEngineOptionLabel(item)
      }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }))
      .map(option => {
      if (!uniqueEngineOptions.has(option.value)) {
         uniqueEngineOptions.add(option.value);
         return `<li class="custom-select__option text-left flex items-center js-option ${option.value == localStorageEngine ? 'active-selected' : ''}" id="jsc-engine-${handleizedString(option.value)}" role="option" data-value="${option.value}" onclick="selectOption(this,'engine')" ${option.value == localStorageEngine ? 'aria-selected="true"' : 'aria-selected="false"'}>
                  <span class="pointer-events-none">${option.label}</span>
               </li>`;
      }
   });

   engineUlElement.removeClass('hide').attr('hidden', true).html(engineOptions.join(''));

   $('.jsc-engine .custom-select__btn').addClass('jsc-enabled-dropdown');
   $('.jsc-engine').find('.custom-select__btn').prop('disabled', false);
}

loadBrandOption = () => {
   // Fetch JSON data from the URL
   var getBrandLocalStorage = getLocalStorage('brand');
   var carDb = brandDb;
   // Populate the "Brand" dropdown
   var brandUlElement = $('.jsc-brand .custom-select__listbox');

   // Get unique brands, sort them alphabetically, and map to HTML options
   var brandOptions = [...new Set(carDb.map(item => item.brand))]
      .sort((a, b) => a.localeCompare(b)) // Sort brands alphabetically
      .map(brand => {
         var handleizedBrand = handleizedString(brand);
         return `<li class="custom-select__option flex items-center js-option ${brand == getBrandLocalStorage ? 'active-selected' : ''}" id="jsc-brand-${handleizedBrand}" role="option" data-value="${brand}" onclick="selectOption(this,'brand')" ${brand == getBrandLocalStorage ? 'aria-selected="true"' : 'aria-selected="false"'}>
                     <span class="pointer-events-none">${brand}</span>
                 </li>`;
      });

   // Update the dropdown with sorted options
   brandUlElement.html(brandOptions.join(''));
};

loadModelOption = (brand) => {
   var carDb = cachedCarDb;

   populateModelHtml(carDb,brand);
   resetModelOption();
   resetEngineOption();

   $('.jsc-engine .custom-select__listbox').addClass('hide').attr('hidden', false).html(blankLi);
   $('.jsc-engine .custom-select__btn').removeClass('jsc-enabled-dropdown');
   $('.jsc-engine').find('.custom-select__btn').prop('disabled', true);

   // reset local storage
   setLocalStorage('model', '');
   setLocalStorage('engine', '');
}

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(".search-brand, .search-model, .search-engine").forEach(input => {
    ["keydown", "keyup", "keypress"].forEach(eventName => {
      input.addEventListener(eventName, function (e) {
        if (e.key === " " || e.code === "Space") {
          e.stopPropagation();
        }
      });
    });

    input.addEventListener("click", e => e.stopPropagation());
    input.addEventListener("mousedown", e => e.stopPropagation());
  });

  const normalizeSearchText = (value) => {
    return String(value || "")
      .toLowerCase()
      .replace(/\s+/g, "");
  };

  const filterDropdown = (searchInputSelector, dropdownSelector) => {
    const searchInput = document.querySelector(searchInputSelector);
    if (!searchInput) return;

    let debounceTimer;
    const safeFilter = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const dropdown = document.querySelector(dropdownSelector);
        if (!dropdown) return;

        const rawQuery = searchInput.value.trim().toLowerCase();
        const query = normalizeSearchText(rawQuery);
        const options = dropdown.querySelectorAll(".custom-select__option");
        if (!options.length) return;

        let matchFound = false;
        options.forEach(option => {
          const text = option.textContent.trim();
          const match =
            normalizeSearchText(text).includes(query) ||
            text.toLowerCase().includes(rawQuery);

          option.style.display = match || rawQuery === "" ? "" : "none";
          if (match) matchFound = true;
        });

        if (rawQuery === "" || matchFound) {
          dropdown.classList.remove("hide");
          dropdown.removeAttribute("hidden");
        } else {
          dropdown.classList.add("hide");
          dropdown.setAttribute("hidden", true);
        }
      }, 100);
    };

    searchInput.addEventListener("input", safeFilter);
    searchInput.addEventListener("keyup", (e) => {
      if (e.key === "Backspace" || e.key === "Delete") safeFilter();
    });
  };

  filterDropdown(".search-brand", ".jsc-brand .custom-select__listbox");
  filterDropdown(".search-model", ".jsc-model .custom-select__listbox");
  filterDropdown(".search-engine", ".jsc-engine .custom-select__listbox");
});

loadEngineOption = (model) => {
   var carDb = cachedCarDb;

   populateEngineHtml(carDb, model);

   resetEngineOption();

   // reset local storage
   setLocalStorage('engine', '');
}

resetBrandOption = () => {
   $('.jsc-brand .custom-select__btn span.text-start').text('Brand...');
   $('.jsc-brand').find('.jsc-check-mark').hide();
   $('.jsc-popup-warning').show();
   $('.find-parts').removeAttr('href target');
   $('.find-parts').attr('aria-disabled', true);
   $('button[name="checkout"]').attr('disabled', true);
   $('.dynamic-cart-btns').css('display', 'none');
}

resetModelOption = () => {
   $('.jsc-model .custom-select__btn span.text-start').text('Model...');
   $('.jsc-model').find('.jsc-check-mark').hide();
   $('.jsc-popup-warning').show();
   $('.find-parts').removeAttr('href target');
   $('.find-parts').attr('aria-disabled', true);
   $('button[name="checkout"]').attr('disabled', true);
   $('.dynamic-cart-btns').css('display', 'none');
}

resetEngineOption = () => {
   $('.jsc-engine .custom-select__btn span.text-start').text('Year...');
   $('.jsc-engine').find('.jsc-check-mark').hide();
   $('.jsc-popup-warning').show();
   $('.find-parts').removeAttr('href target');
   $('.find-parts').attr('aria-disabled', true);
   $('button[name="checkout"]').attr('disabled', true);
   $('.dynamic-cart-btns').css('display', 'none');
   renameEngineSelectorToYear();
}



/* =========================================================
   09. CAR SELECTION / RESET LOGIC
========================================================= */

selectOption = (e, key) => {
    var _this = $(e);
    var value = _this.attr('data-value');

    // Update selected option state
    _this
     .siblings('.custom-select__option')
     .removeClass('active-selected')
     .attr('aria-selected', 'false');

    _this
     .addClass('active-selected')
     .attr('aria-selected', 'true');

    // Set Local storage
    setLocalStorage(key, value);

    // Enable checkmark
    enableCheckMark(key);

    // Enable model option
    if (key == 'brand') {
        loadModelOption(value);
    }

    // Enable engine option
    if (key == 'model') {
        loadEngineOption(value);
    }

    // If Engine is selected, fetch and log the Car ID

    if (key == 'engine') {
      $('.jsc-popup-warning').hide();

      const finalHref = buildAkrapovicCollectionUrl();

      $('.find-parts').attr('href', finalHref);
      $('.find-parts').removeAttr('target');
      $('.find-parts').attr('aria-disabled', false);
      syncAkrapovicActionButtons();
      $('button[name="checkout"]').attr('disabled', false);
      $('.dynamic-cart-btns').css('display', 'block');

      saveCarDetails(value); // This function saves car details

      // Fetch and log Car ID immediately
      fetchAndLogCarId();
    }
}

const fetchAndLogCarId = () => {
  var brand = getLocalStorage('brand');
  var model = getLocalStorage('model');
  var engine = getLocalStorage('engine');

  if (!brand || !model || !engine || !cachedCarDb || cachedCarDb.length === 0) return;

  var engineArray = engine.split('--');

  var selectedCar = cachedCarDb.find(car =>
    carMatchesSelection(car, brand, model, engineArray)
  );

  if (!selectedCar || !selectedCar.carId) return;

  localStorage.setItem("previousCarId", selectedCar.carId);

  const url = new URL(window.location.href);
  url.searchParams.set("carId", selectedCar.carId);
  window.history.replaceState({}, "", url.toString());
  document.dispatchEvent(new CustomEvent('autoclinic:carIdChanged'));

  loadHeaderCarInfo();
  loadCarInfoToLIProperties();
  checkCompatibility();
  filterAddOnsForSelectedCar();
};

setLocalStorage = (key, value) => {
   localStorage.setItem(key, value);
}

getLocalStorage = (key) => {
   const value = localStorage.getItem(key);
   return value === null || value === '' ? '' : value;
}

function refreshSelectedCarUIFromStorage() {
  loadMyCarModel();
  loadHeaderCarInfo();
  loadCarInfoToLIProperties();
  onloadPopulateCarFilterFromLocalStorage();
  checkCompatibility();
}

saveCarDetails = (engineValue) =>{
   var getBrandLocalStorage = getLocalStorage('brand');
   var getModelLocalStorage = getLocalStorage('model');
   var engineValueArray = engineValue.split('--');
   var axleFa = [];
   var axleRa = [];
   var contentOfDelivery = [];
   var vehicleHasElectronicDamper = [];
   var noteProduct = [];
   var diameterFa = [];
   var strutDiameterFrontAxle = [];
   var rearAxleType = [];
   var noteCar = [];
   var frontAxleStruts = [];

   var carDb = cachedCarDb;

   // Filter the JSON data for the selected "Brand"
   var filteredData = carDb.filter(function(item) {
      return item.make === getBrandLocalStorage && item.model == getModelLocalStorage && item.carName === engineValueArray[0] && item['power(kW)'] === engineValueArray[1] && item.cyl === engineValueArray[2] && item.drivetype === engineValueArray[3];
   });

   for (let i = 0; i < filteredData.length; i++) {
      const element = filteredData[i];
      axleFa.push(element['axleLoadFa (kg)']);
      axleRa.push(element['axleLoadRa (kg)']);
      contentOfDelivery.push(element['contentOfDelivery']);
      vehicleHasElectronicDamper.push(element['vehicleHasElectronicDamper']);
      noteProduct.push(element['note (product)']);
      diameterFa.push(element['diameterFa (mm)']);
      strutDiameterFrontAxle.push(element['strutDiameterFrontAxle (mm)']);
      rearAxleType.push(element['rearAxleType']);
      noteCar.push(element['note (carAssignment)']);
      frontAxleStruts.push(element['frontAxleStruts']);
   }

   setLocalStorage('axle weight fa', Array.from(new Set(axleFa)));
   setLocalStorage('axle weight ra', Array.from(new Set(axleRa)));
   setLocalStorage('content of delivery', Array.from(new Set(contentOfDelivery)));
   setLocalStorage('vehicle has electronic damper', Array.from(new Set(vehicleHasElectronicDamper)));
   setLocalStorage('note (product)', Array.from(new Set(noteProduct)));
   setLocalStorage('diameter fa (mm)', Array.from(new Set(diameterFa)));
   setLocalStorage('strut diameter front axle (mm)', Array.from(new Set(strutDiameterFrontAxle)));
   setLocalStorage('rear axle type', Array.from(new Set(rearAxleType)));
   setLocalStorage('note (car assignment)', Array.from(new Set(noteCar)));
   setLocalStorage('front axle struts', Array.from(new Set(frontAxleStruts)));
}

disabledAllSelection = () => {
   // Model
   $('.jsc-model .custom-select__btn').removeClass('jsc-enabled-dropdown');
   $('.jsc-model .custom-select__listbox').addClass('hide').attr('hidden', false).html(blankLi);
   $('.jsc-model').find('.custom-select__btn').prop('disabled', true);

   // Engine
   $('.jsc-engine .custom-select__btn').removeClass('jsc-enabled-dropdown');
   $('.jsc-engine .custom-select__listbox').addClass('hide').attr('hidden', false).html(blankLi);
   $('.jsc-engine').find('.custom-select__btn').prop('disabled', true);

   // reset local storage
   setLocalStorage('brand', '');
   setLocalStorage('model', '');
   setLocalStorage('engine', '');
}

clearSelection = () => {
   resetBrandOption();
   resetModelOption();
   resetEngineOption();

   disabledAllSelection();
}

onloadPopulateCarFilterFromLocalStorage = () => {
  var getBrandLocalStorage = getLocalStorage('brand');
  var getModelLocalStorage = getLocalStorage('model');
  var getEngineLocalStorage = getLocalStorage('engine');

  var carDb = cachedCarDb;

  if (getBrandLocalStorage) {
    // Load Model option if Brand is in local storage
    populateModelHtml(carDb, getBrandLocalStorage, getModelLocalStorage);

    // Styling changes to brand dropdown
    enableCheckMark('brand');
    $('.jsc-brand .custom-select__btn span.text-start').text(getBrandLocalStorage);
  }

  if (getModelLocalStorage) {
    // Load Engine option if Model is in local storage
    populateEngineHtml(carDb, getModelLocalStorage, getEngineLocalStorage);

    // Styling changes to model dropdown
    enableCheckMark('model');
    $('.jsc-model .custom-select__btn span.text-start').text(getModelLocalStorage);
  }

  if (getEngineLocalStorage) {
    // Styling changes to engine dropdown
    enableCheckMark('engine');
    $('.jsc-engine .custom-select__btn span.text-start').text(getEngineLocalStorage.replaceAll('--', '-'));
    $('.jsc-popup-warning').hide();

    const finalHref = buildAkrapovicCollectionUrl();

    $('.find-parts').attr('href', finalHref);
    $('.find-parts').removeAttr('target');
    $('.find-parts').attr('aria-disabled', false);
    syncAkrapovicActionButtons();

    $('button[name="checkout"]').attr('disabled', false);
    $('.dynamic-cart-btns').css('display', 'block');
  }
}

enableCheckMark = (key) => {
   $(`#jsc-${key}`).find('.jsc-check-mark').show();
}

changeVehicleTrigger = (e, triggerFrom) =>{
  carPopupScrollY = window.scrollY || document.documentElement.scrollTop || 0;
   // Hide my car model section
   $('jsc-popup-body').find('jsc-popup-my-model').hide();
   // Show selection dropdown
   $('jsc-popup-select').show();
   $('body').addClass('model-open');
   // Change popup header 
   $('.jsc-popup-header').text('Select Your Vehicle');

   if (triggerFrom == 'page') {
      openCarPopup(e);
   }
}


/* =========================================================
   10. SELECTED VEHICLE UI / POPUP DISPLAY
========================================================= */

function renderSelectedCarDetails() {
   var brandName = getLocalStorage('brand');
   var modelValue = getLocalStorage('model');
   var engineValue = getLocalStorage('engine');

   if (!(brandName && modelValue && engineValue)) return false;

   var html = `<div class="jsc-my-model-detail-brand-model">
               <span class="h6">${brandName} ${modelValue}</span>
            </div>
            <div class="jsc-my-model-detail-engine">
               <span>Year: ${engineValue}</span>
            </div>`;

   var html2 = `<div class="custom-model-detail jsc-my-model-detail">
                  <span class="fontWeight-9">Products compatible for: <span class="mobile-only"></span> ${brandName} ${modelValue}</span>
                  <span>Year: ${engineValue}</span>
               </div>`;

   $('.jsc-my-model-detail').html(html);
   $('.jsc-my-model-detail-v2').html(html2);

   return true;
}

loadMyCarModel = () =>{
   var brandName = getLocalStorage('brand');
   var modelValue = getLocalStorage('model');
   var engineValue = getLocalStorage('engine');
   if (brandName && modelValue && engineValue) {
      renderSelectedCarDetails();

      // Only hide selector when popup has selected-car content available
      if ($('jsc-popup-my-model').length) {
         $('jsc-popup-select').hide();
         $('jsc-popup-my-model').show();
         $('.jsc-popup-header').text('Search parts for my vehicle');
      } else {
         $('jsc-popup-select').show();
         $('.jsc-popup-header').text('Select Your Vehicle');
      }
   } else {
      $('jsc-popup-my-model').hide();

     const carModelPaths = [
      "/pages/my-car-model",
      "/en-sg/pages/my-car-model",
      "/en-my/pages/my-car-model",
      "/th-th/pages/my-car-model"
      ];

      if (carModelPaths.includes(window.location.pathname)) {
      const triggerElement = document.querySelector(".jsc-header-car");
         if (triggerElement) {
            triggerElement.click();
         }
      }
   }
}
loadHeaderCarInfo = () =>{
   if (shouldRemoveThaiExhaustFitment()) {
      $('.jsc-header-car-title, .menu-jsc-header-car-title').text('');
      return;
   }

   var brandName = getLocalStorage('brand');
   var modelValue = getLocalStorage('model');
   var engineValue = getLocalStorage('engine');

   if (brandName && modelValue && engineValue){
      var brandNameAndModel = `${brandName} ${modelValue}`;
      var brandNameAndModel2 = `${brandName} ${modelValue} ${engineValue}`;
      $('.jsc-header-car-title').text(brandNameAndModel);
      $('.menu-jsc-header-car-title').text(brandNameAndModel2);
   }
}
let carPopupScrollY = 0;

function openCarPopup(e) {
  if (e && e.preventDefault) {
    e.preventDefault();
    e.stopPropagation();
  }

  carPopupScrollY = window.scrollY || document.documentElement.scrollTop || 0;

  $('jsc-popup').show();
  $('#myPopup').show();
  $('body').addClass('model-open');

  requestAnimationFrame(() => {
    window.scrollTo(0, carPopupScrollY);
  });

  const hasCar =
    getLocalStorage('brand') &&
    getLocalStorage('model') &&
    getLocalStorage('engine');

  if (hasCar) {
    renderSelectedCarDetails();
    $('jsc-popup-select').hide();
    $('jsc-popup-my-model').show();
    $('.jsc-popup-header').text('Search parts for my vehicle');
  } else {
    $('jsc-popup-select').show();
    $('jsc-popup-my-model').hide();
    $('.jsc-popup-header').text('Select Your Vehicle');
  }
}

/* =========================================================
   AUTO OPEN CAR SELECTOR ON COLLECTION / MY CAR MODEL PAGE
========================================================= */

function hasSelectedVehicle() {
  var brand = getLocalStorage('brand');
  var model = getLocalStorage('model');
  var engine = getLocalStorage('engine');

  return Boolean(
    String(brand || '').trim() &&
    String(model || '').trim() &&
    String(engine || '').trim()
  );
}

function isCollectionPage() {
  return window.location.pathname.includes('/collections/');
}

function isMyCarModelPage() {
  var carModelPaths = [
    '/pages/my-car-model',
    '/en-sg/pages/my-car-model',
    '/en-my/pages/my-car-model',
    '/th-th/pages/my-car-model'
  ];

  return carModelPaths.includes(window.location.pathname);
}

function shouldAutoOpenCarSelector() {
  return !hasSelectedVehicle() && (isCollectionPage() || isMyCarModelPage());
}

function autoOpenCarSelectorWhenNeeded() {
  if (!shouldAutoOpenCarSelector()) return;

  setTimeout(function () {
    if (hasSelectedVehicle()) return;

    if (typeof openCarPopup === 'function') {
      openCarPopup();
      return;
    }

    var popup = document.getElementById('myPopup');
    if (popup) {
      popup.style.display = 'block';
      document.body.classList.add('model-open');
    }
  }, 500);
}

document.addEventListener('DOMContentLoaded', function () {
  autoOpenCarSelectorWhenNeeded();
});

/* =========================================================
   11. COMPATIBILITY TABLE
========================================================= */

onLoadCreateCompatibleTable = () => {
   const campatibilityTableContainer = $('#compatiblility-table');
   if (!campatibilityTableContainer.length) return;

   // Retrieve SKU filter from URL
   const urlParams = new URLSearchParams(window.location.search);
   const skuFilter = urlParams.get('filter.p.m.json.handle');

   if (!skuFilter) {
       campatibilityTableContainer.parents('.jsc-section').hide();
       return;
   }

   // Clear all existing product entries in the table to start fresh
   const tbody = campatibilityTableContainer.find('tbody');
   tbody.empty();

   // Apply strict filtering based on the SKU field in cachedCarDb
   const filteredData = cachedCarDb.filter(element => element.jsonHandle === skuFilter);

   // If no items match the filter, hide the compatibility table section
   if (filteredData.length === 0) {
       campatibilityTableContainer.parents('.jsc-section').hide();
       return;
   }

   // Generate table rows only for filtered items
   const tableHTMLArray = filteredData.map(element => 
       `<tr><td>${element.make}</td><td>${element.model}</td><td>${element.carName}</td>
       <td>${element.chassis}</td><td>${element.aspiration}</td><td>${element['power(kW)']}</td>
       <td>${element.ccm}</td><td>${element.cyl}</td><td>${element.drivetype}</td></tr>`
   );

   // Append filtered results to the table
   tableHTMLArray.forEach(row => tbody.append(row));
};

loadTableRow = (htmlArray, page) => {

    const campatibilityTableContainer = $('#compatiblility-table');
    if (!campatibilityTableContainer.length || htmlArray.length === 0) {
        campatibilityTableContainer.parents('.jsc-section').hide();
        return;
    }

    // Clear previous rows and pagination
    const tbody = campatibilityTableContainer.find('tbody');
    tbody.empty();
    campatibilityTableContainer.find('.jsc-pagination-pages').empty();

    const perPageLimit = 15;
    const totalPaginationNum = Math.ceil(htmlArray.length / perPageLimit);
    const arrayStartIndex = (page - 1) * perPageLimit;
    const arrayEndIndex = Math.min(page * perPageLimit, htmlArray.length);

    // Append rows to the table
    for (let index = arrayStartIndex; index < arrayEndIndex; index++) {
        tbody.append(htmlArray[index]);
    }

    // Update pagination
    updatePaginationIndex(totalPaginationNum, page, htmlArray);
};

// Function to update pagination index
updatePaginationIndex = (totalPage, activePage, htmlArray) => {
   const paginationContainer = $('#jsc-pagination-container');
   const paginationPages = paginationContainer.find('.jsc-pagination-pages');

   // Loop through the total number of pages
   for (let index = 1; index <= totalPage; index++) {
       // If it's the active page, add a current page class
       if (index === activePage) {
           paginationPages.append(`<span class="jsc-currentPage jsc-pagination-index m-l-10 m-r-10">${index}</span>`);
       } else {
           // Add pagination index with onclick function to paginate
           paginationPages.append(`<span class="jsc-pagination-index cursor m-l-10 m-r-10 " onclick="paginate('${htmlArray.join('---')}',${index})">${index}</span>`);
       }
   }
};

// Function to paginate and load new table rows
paginate = (htmlString, pageIndex) => {
   const campatibilityTableContainer = $('#compatiblility-table');
   const htmlArray = htmlString.split('---');

   // Remove existing table rows and pagination
   campatibilityTableContainer.find('tbody').children().remove();
   campatibilityTableContainer.find('.jsc-pagination-pages').children().remove();

   // Load new table rows with pagination
   loadTableRow(htmlArray, pageIndex);
};
// Compatibility Table ends here

// Specification tab starts here
/* =========================================================
   12. PRODUCT SPECIFICATIONS
========================================================= */

onLoadShowSpecificationContent = () => {
   const normalize = (val) => String(val || '').trim().toLowerCase();
   // Get selected car details from local storage
   const brand = getLocalStorage('brand');
   const model = getLocalStorage('model');
   const engine = getLocalStorage('engine');
   const engineArray = engine.split('--');

   // Get current PDP sku
   const specificationElement = $('#jsc-specification');
   const productSku = specificationElement.attr('data-sku');

   let specificationHtml = '';

   // Filter the product to match the current product sku and selected car filter from the Database
   const filteredData = cachedCarDb.filter((item) =>
      carMatchesSelection(item, brand, model, engineArray) &&
      carDbColumn.some(type => item[type]?.includes(productSku))
   );

   // Loop through the filtered data array
   filteredData.forEach(element => {
      //Build the HTML content
      specificationHtml += `<strong>Axle Load FA (kg):</strong>${element['axleLoadFa (kg)']}<br>
                           <strong>Axle Load RA (kg):</strong>${element['axleLoadRa (kg)']}<br>
                           <strong>Lowering FA (mm):</strong>${element['loweringFa (mm)']}<br>
                           <strong>Lowering RA (mm):</strong>${element['loweringRa (mm)']}<br>`;
   });

   // Append HTML to Specification section/tab/accordion
   specificationElement.append(specificationHtml);

}

const buildSkuParameter = (skuArray) => {
  return skuArray.map((sku) => `filter.p.m.json.handle=${sku}`).join('&');
};

onloadDateRange = () => {
   var today = new Date();
   var startDate = new Date(today);
   var startDay = parseInt($('.jsc-date-range').attr('data-start'));
   startDate.setDate(startDate.getDate() + startDay);
   var endDate = new Date(today);
   var endDay = parseInt($('.jsc-date-range').attr('data-end'));
   endDate.setDate(endDate.getDate() + endDay);

   var dd_start = String(startDate.getDate()).padStart(2, '0');
   var dd_end = String(endDate.getDate()).padStart(2, '0');
   var monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
   var month_start = monthNames[startDate.getMonth()];
   var month_end = monthNames[endDate.getMonth()];
   var formattedStartDate = dd_start + ' ' + month_start;
   var formattedEndDate = dd_end + ' ' + month_end;

   $('.jsc-range-date').text(formattedStartDate + ' - ' + formattedEndDate);
 }
 // Function to update links based on the provided linkType


/* =========================================================
   13. FILTER PAGE LINKS / RESULT COUNTS
========================================================= */

const updateLinks = (linkType) => {
   const normalize = (val) => String(val || '').trim().toLowerCase();
   // Retrieve data from local storage
   const brand = getLocalStorage('brand');
   const model = getLocalStorage('model');
   const engine = getLocalStorage('engine');
   const engineArray = engine.split('--');
   const skuArray = [];

   // Filter car database based on selected criteria
   const filteredData = cachedCarDb.filter((item) =>
      carMatchesSelection(item, brand, model, engineArray)
   );

   // Check if any matching data is found
   if (filteredData.length === 0) {
      return;
   }

   // Extract SKU data for the given linkType from filtered data
   skuArray.push(
      ...filteredData
         .map((carData) => carData[linkType]) // Extracting the value first
         .filter(sku => sku !== '' && sku !== undefined && (typeof sku === 'string' || typeof sku === 'number')) // Filter out empty string, undefined and non-string/number values
         .map((sku) => typeof sku === 'string' ? sku.split(/ \| |\|/) : [sku.toString()]) // Splitting only if defined and a string
         .flat()
   );

   // Build SKU parameter and construct filtered collection URL
   const skuParameter = buildSkuParameter(skuArray);
   const filteredCollection = `/collections/all?${skuParameter}`;

   // Update the href attribute of the corresponding element with the filtered collection URL
   const elementId = `jsc-type-${linkType.toLowerCase()}`;
   if ($('#' + elementId).length > 0) {
      $('#' + elementId).attr('href', filteredCollection);
   }
 };

 // Function to append filter page links for all specified link types
 const appendFilterPageLinks = () => {
   // Iterate through each link type and call updateLinks function
   carDbColumn.forEach((linkType) => updateLinks(linkType));
 };

// Function to update links based on the provided linkType
const updateLinkList = (linkType, linkUrl, linkClass) => {
   const normalize = (val) => String(val || '').trim().toLowerCase(); // Add this inside the function

   // Retrieve data from local storage
   const brand = getLocalStorage('brand');
   const model = getLocalStorage('model');
   const engine = getLocalStorage('engine');
   const engineArray = engine.split('--');
   const skuArray = [];

   // Filter car database based on selected criteria
   const filteredData = cachedCarDb.filter((item) =>
      carMatchesSelection(item, brand, model, engineArray)
   );

   if (filteredData.length === 0) {
      return;
   }

   // Extract SKU data for the given linkType from filtered data
   skuArray.push(
      ...filteredData
         .map((carData) => carData[linkType])
         .filter(sku => sku !== '' && sku !== undefined && (typeof sku === 'string' || typeof sku === 'number'))
         .map((sku) => typeof sku === 'string' ? sku.split(/ \| |\|/) : [sku.toString()])
         .flat()
   );

   const uniqueSkuArray = [...new Set(skuArray)];

   const skuParameter = buildSkuParameter(uniqueSkuArray);
   const filteredCollection = `${linkUrl}?${skuParameter}`;
   const skuLength = uniqueSkuArray.length;

   const $linkElements = $(`.${linkClass}`);
   if (skuLength) {
      $linkElements.attr('href', filteredCollection);
   } else {
      $linkElements.addClass('jsc-disabled-link');
   }

   setTimeout(() => {
      const cardLinkLists = document.querySelectorAll(".card__link-list");
      cardLinkLists.forEach(cardLinkList => {
         cardLinkList.classList.add("active");
         const activeLinks = cardLinkList.querySelectorAll("li a:not(.jsc-disabled-link)");

         if (activeLinks.length > 0) {
            activeLinks.forEach(link => {
               link.closest("li").classList.add("active-jsc-link");
            });
         } else {
            cardLinkList.classList.add("no-available");
         }
      });
   }, 200);

   $linkElements.find('.jsc-result-count').text(`(${skuLength})`).attr('data-count', skuLength);
};

function updateLinkCounts() {
  const links = document.querySelectorAll('a.card__link-list-item');

  links.forEach(link => {
    const href = link.getAttribute('href') || '';
    const count = href.split('filter.p.m.json.handle=').length - 1;

    const originalText =
      link.dataset.originalText ||
      link.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();

    link.dataset.originalText = originalText;

    if (count > 0) {
      link.textContent = `${originalText} (${count})`;
    }
  });
}

function runLinkCountsFast() {
  updateLinkCounts();

  let attempts = 0;
  const maxAttempts = 20;

  const timer = setInterval(() => {
    updateLinkCounts();
    attempts++;

    if (attempts >= maxAttempts) {
      clearInterval(timer);
    }
  }, 100);
}

function appendFilterPageLinkList() {
  const linkInfo = [];
  const $linkList = $('.jsc-link-list-car');

  $linkList.each(function () {
    const $this = $(this);

    linkInfo.push({
      type: toCamelCase($this.attr('data-part')),
      url: $this.attr('data-url'),
      class: `jsc-link-list-${$this.attr('data-part-handleize')}`
    });
  });

  linkInfo.forEach(({ type, url, class: className }) => updateLinkList(type, url, className));

  runLinkCountsFast();
}

document.addEventListener('DOMContentLoaded', function () {
  runLinkCountsFast();

  window.addEventListener('popstate', runLinkCountsFast);
  window.addEventListener('pushstate', runLinkCountsFast);
  window.addEventListener('replacestate', runLinkCountsFast);
});

// Polyfill for pushState and replaceState events
(function(history) {
    const pushState = history.pushState;
    const replaceState = history.replaceState;

    history.pushState = function(state, title, url) {
        const result = pushState.apply(history, arguments);
        window.dispatchEvent(new Event('pushstate'));
        return result;
    };

    history.replaceState = function(state, title, url) {
        const result = replaceState.apply(history, arguments);
        window.dispatchEvent(new Event('replacestate'));
        return result;
    };
})(window.history);

// Function to convert string into camelcase
const toCamelCase = (str) => {
   // Convert to camelCase
   // return str.replace(/\s(.)/g, (_, char) => char.toUpperCase())
   //           .replace(/\s/g, '')
   //           .replace(/^(.)/, (_, char) => char.toLowerCase());
   return str.replace(/-/g, ' ')
             .replace(/\s(.)/g, (_, char) => char.toUpperCase())
             .replace(/\s/g, '')
             .replace(/^(.)/, (_, char) => char.toLowerCase())
             .replace(/\(/g, ' (')
};

// Function to get all values of a URL parameter
const getParameterValues = (parameter) => {
   const urlParams = new URLSearchParams(window.location.search);
   return urlParams.getAll(parameter);
};

// Function to append Car filter into the CP filter form so doesn't remove the car filter when user interact with other filter 
appendURLFilterParamAsHiddenInputInCPFilter = () =>{
   // Get the value of filter.p.m.json.handle parameter
   const handleValue = getParameterValues('filter.p.m.json.handle');
   var htmlString = '';

   for (let i = 0; i < handleValue.length; i++) {
      const element = handleValue[i];
      htmlString += `<input type="hidden" name="filter.p.m.json.handle" value="${element}">`;
   }

   //Append the hidden input into the filter form
   if ($('.hidden-car-filter-value').length) {
      $('.hidden-car-filter-value').append(htmlString);  
   }
}

hideCPFilterOption = () =>{
   var axleFaArray = getLocalStorage('axle weight fa').split(',');
   var axleRaArray = getLocalStorage('axle weight ra').split(',');
   var contentOfDeliveryArray = getLocalStorage('content of delivery').split(',');
   var vehicleHasElectronicDamperArray = getLocalStorage('vehicle has electronic damper').split(',');
   var noteProductArray = getLocalStorage('note (product)').split(',');
   var diameterFaArray = getLocalStorage('diameter fa (mm)').split(',');
   var strutDiameterFrontAxleArray = getLocalStorage('strut diameter front axle (mm)').split(',');
   var rearAxleTypeArray = getLocalStorage('rear axle type').split(',');
   var noteCarArray = getLocalStorage('note (car assignment)').split(',');
   var frontAxleStrutsArray = getLocalStorage('front axle struts').split(',');

   var filterValueLi = $('.jsc-filter-check');

   filterValueLi.each(function(){
      var _this = $(this);
      var attrVal = _this.attr('data-filter-value');
      if (axleFaArray.includes(attrVal)) {
         _this.show();
      }
      if (axleRaArray.includes(attrVal)) {
         _this.show();
      }
      if (contentOfDeliveryArray.includes(attrVal)) {
         _this.show();
      }
      if (vehicleHasElectronicDamperArray.includes(attrVal)) {
         _this.show();
      }
      if (noteProductArray.includes(attrVal)) {
         _this.show();
      }
      if (diameterFaArray.includes(attrVal)) {
         _this.show();
      }
      if (strutDiameterFrontAxleArray.includes(attrVal)) {
         _this.show();
      }
      if (rearAxleTypeArray.includes(attrVal)) {
         _this.show();
      }
      if (noteCarArray.includes(attrVal)) {
         _this.show();
      }
      if (frontAxleStrutsArray.includes(attrVal)) {
         _this.show();
      }
   });
}

hideCpFilterWhenEmpty = () =>{
   var $grandparents = $('.jsc-cp-filter-parent');

   // Iterate through each grandparent
   $grandparents.each(function() {
      var $grandparent = $(this);
      var anyShown = true;

      // Find all grandchildren elements within the current grandparent
      var $grandchildren = $grandparent.find('.jsc-filter-check');

      // Iterate through each grandchild
      $grandchildren.each(function() {
          // Check if the current grandchild is hidden
          if ($(this).css('display') !== 'none') {
              anyShown = false;
              return false; // Exit the loop early since we found a hidden grandchild
          }
      });

      // If any grandchildren are hidden, adjust the height of the current grandparent

      if (anyShown) {
         //  $grandparent.hide();
      }
  });
}

//Load Selected car into line item properties Start
loadCarInfoToLIProperties = () =>{
   if (shouldRemoveThaiExhaustFitment()) {
      clearSelectedCarLineItemProperties();
      return;
   }

   var brandName = getLocalStorage('brand');
   var modelValue = getLocalStorage('model');
   var engineValue = getLocalStorage('engine');

   if (brandName && modelValue && engineValue){
      var brandNameAndModel = `${brandName} ${modelValue}`;
      var engineFormatted = engineValue.replaceAll('--','-');

      $('#jsc-selected-brand-model, .jsc-selected-brand-model').val(brandNameAndModel);
      $('#jsc-selected-engine, .jsc-selected-engine').val(engineFormatted);
   }

   var brandNameAndModelValue = `${brandName} ${modelValue}`;
   brandNameAndModelValue = brandNameAndModelValue.split('(')[0].trim();
   $('.jsc-selected-model-title').text(brandNameAndModelValue);
}
//Load Selected car into line item properties End

/* =========================================================
   14. COMPATIBILITY / FITMENT CHECKING
========================================================= */

const checkCompatibility = () => {
   // Get the compatible part element
   const compatiblePartElement = $('.compatible-part');
   // Check if compatible part element exists
   if (!compatiblePartElement.length) return;

   // Get brand, model, and engine details from local storage
   const brand = getLocalStorage('brand');
   const model = getLocalStorage('model');
   const engine = getLocalStorage('engine');
   const engineArray = engine.split('--');

   // Check compatibility of the part with the selected criteria
   const isCompatiblePart = checkPartCompatibility(cachedCarDb, brand, model, engineArray, compatiblePartElement);
   // Update compatibility UI based on the result
   updateCompatibilityUI(isCompatiblePart);
};

// Function to fetch car database JSON data
const fetchCarDatabase = (callback) => {
   fetchCarDb().then(callback);
};

// Function to check compatibility of the part with the car database
const checkPartCompatibility = (carDb, brand, model, engineArray, compatiblePartElement) => {
   // Iterate through each car in the database
   for (const car of carDb) {
       // Check if the part SKU matches and criteria match
       const isCompatiblePart = carDbColumn.some(type => car[type]?.toString().includes(compatiblePartElement.attr('data-sku')));
       if (isCompatiblePart && carMatchesSelection(car, brand, model, engineArray)) {
           // Return true if part is compatible
           return true;
       }
   }
   // Return false if part is not compatible
   return false;
};

// Function to update compatibility UI based on the result
let isPartCompatible = true; // track compatibility status globally

const updateCompatibilityUI = (isCompatiblePart) => {
    isPartCompatible = isCompatiblePart;

    const carSelected = $('.jsc-my-model-detail span').text().trim() !== '';

    if (isCompatiblePart) {
        $('.jsc-positive-compatibility').show();
        $('.jsc-negative-compatibility').hide();
    } else {
        $('.jsc-positive-compatibility').hide();
        $('.jsc-negative-compatibility').show();

        if (!carSelected) {
            $('.jsc-negative-compatibility span').text("Please select your vehicle first to confirm item compatibility.");
            $('.btn_cv').text("Select your vehicle here");
        } else {
            $('.jsc-negative-compatibility span').text("This part doesn't fit");
        }
    }

    $('.checking-text').hide();
};

// Add to Cart button handler
document.addEventListener("DOMContentLoaded", function () {
  const popup = document.getElementById("myPopup");
  const addToCartBtn = document.querySelector('button[name="add"]');

  // Basic "car selected" check (same logic style you already use)
  function isCarSelected() {
    const brand = (localStorage.getItem("brand") || "").trim();
    const model = (localStorage.getItem("model") || "").trim();
    const engine = (localStorage.getItem("engine") || "").trim();

    // fallback UI check (in case localStorage is delayed)
    const uiHasText = ($('.jsc-my-model-detail span').text() || "").trim() !== "";

    return (brand && model && engine) || uiHasText;
  }

   function openCarSelectorPopup() {
    // Prefer your existing openCarPopup() because it also adds body class
    if (typeof openCarPopup === "function") {
      openCarPopup(e);
      return;
    }

    // Fallback: manually show the popup
    if (popup) {
      popup.style.display = "block";
      document.body.classList.add("model-open");
    }
  }

  if (!addToCartBtn) return;

  addToCartBtn.addEventListener("click", function (e) {
    // If no selected car, block add to cart and open car selector popup
    if (!isCarSelected()) {
      e.preventDefault();
      e.stopPropagation();

      // Show your warning text (optional)
      const msgEl = document.querySelector(".jsc-negative-compatibility span");
      if (msgEl) {
        msgEl.textContent = "Please select your vehicle first to confirm item compatibility.";
      }

      openCarSelectorPopup();
      return;  
    }

    // If car is selected but part is not compatible -> keep your existing behavior if you want:
    const errorText = document.querySelector('.jsc-negative-compatibility span')?.textContent.trim();

    if (
      typeof isPartCompatible !== "undefined" &&
      !isPartCompatible &&
      errorText === "This part doesn't fit"
    ) {  }
  }, true);
});

// End of compatible part

$(document).on('click', '#livechat, a[href="#livechat"]', function(e) {
  e.preventDefault();

  // MY webshop
  if (window.location.pathname.startsWith('/en-my')) {
    window.location.href = '/en-my/pages/contact';
    return;
  }

  // Other markets
  if (typeof $respond !== 'undefined') {
    $respond.do("chat:open");
  }
});


/* =========================================================
   15. CART / ADD TO CART / EXHAUST ADD-ONS
========================================================= */

function getCurrentProductSku() {
   return String(
      $('.compatible-part').attr('data-sku') ||
      $('#jsc-specification').attr('data-sku') ||
      ''
   ).trim();
}

const HIDE_ADD_ONS_FOR_CAR_IDS = new Set(['606', '611']);

function getSelectedCarIdForAddOns() {
   const urlCarId = getCarIdFromURL();
   if (urlCarId) return String(urlCarId).trim();

   const previousCarId = localStorage.getItem('previousCarId');
   if (previousCarId) return String(previousCarId).trim();

   const brand = getLocalStorage('brand');
   const model = getLocalStorage('model');
   const engine = getLocalStorage('engine');

   if (!brand || !model || !engine || !cachedCarDb || !cachedCarDb.length) {
      return '';
   }

   const engineArray = engine.split('--');
   const selectedCar = cachedCarDb.find(car =>
      carMatchesSelection(car, brand, model, engineArray)
   );

   return selectedCar && selectedCar.carId ? String(selectedCar.carId).trim() : '';
}

function shouldHideAddOnsForSelectedCar() {
   return HIDE_ADD_ONS_FOR_CAR_IDS.has(getSelectedCarIdForAddOns());
}

function installHiddenAddOnsCss() {
   if (document.getElementById('autoclinic-hidden-add-ons-css')) return;

   const style = document.createElement('style');
   style.id = 'autoclinic-hidden-add-ons-css';
   style.textContent = `
      .autoclinic-force-hide-add-ons .add-ons-grids,
      .autoclinic-force-hide-add-ons .jsc-add-ons,
      .autoclinic-force-hide-add-ons .bundle,
      .autoclinic-force-hide-add-ons [class*="add-on"],
      .autoclinic-force-hide-add-ons [class*="add_on"] {
         display: none !important;
      }
   `;

   document.head.appendChild(style);
}

function clearSelectedAddOns() {
   $('.jsc-add-ons.active').each(function () {
      appendTotalAmountItem($(this), true);
   });

   $('.jsc-add-ons')
      .removeClass('active')
      .find('.jsc-selected-button')
      .hide();
}

function getAddOnsSectionCandidates() {
   const candidates = new Set();

   $('.add-ons-grids, .jsc-add-ons, .bundle, [class*="add-on"], [class*="add_on"]').each(function () {
      const section = $(this).closest('.shopify-section, section, .jsc-section, .bundle, .product-info__block, .accordion, details, div')[0];
      if (section) candidates.add(section);
   });

   $('body *').filter(function () {
      const text = $(this).children().length ? '' : $(this).text().trim().toLowerCase();
      return text === 'optional upgrades' || text === 'add ons' || text === 'add-ons';
   }).each(function () {
      const section = $(this).closest('.shopify-section, section, .jsc-section, .product-info__block, .accordion, details, div')[0];
      if (section) candidates.add(section);
   });

   return Array.from(candidates);
}

function setAddOnsSectionVisible(shouldShow) {
   installHiddenAddOnsCss();
   const sections = getAddOnsSectionCandidates();

   if (shouldShow) {
      document.body.classList.remove('autoclinic-force-hide-add-ons');
      sections.forEach(section => {
         $(section).show().removeAttr('hidden data-autoclinic-hidden-add-ons');
      });
      return;
   }

   document.body.classList.add('autoclinic-force-hide-add-ons');
   clearSelectedAddOns();
   $('.bundle .required').removeClass('required alert');
   $('.bundle .alert-message, .bundle .required-svg').addClass('d-none');
   sections.forEach(section => {
      $(section).hide().attr({
         hidden: true,
         'data-autoclinic-hidden-add-ons': 'true'
      });
   });
   calculateTotalAmount();
}

function getAllowedAddOnSkusForSelectedCar() {
   const brand = getLocalStorage('brand');
   const model = getLocalStorage('model');
   const engine = getLocalStorage('engine');
   const currentProductSku = getCurrentProductSku();

   if (!brand || !model || !engine || !currentProductSku || !cachedCarDb || !cachedCarDb.length) {
      return null;
   }

   const engineArray = engine.split('--');
   const selectedRows = cachedCarDb.filter(car =>
      carMatchesSelection(car, brand, model, engineArray)
   );

   if (!selectedRows.length) {
      return null;
   }

   return new Set(
      selectedRows
         .map(car => car.salesId || car.akrapovic || car['racingSystem (noCertification)'] || car.carId)
         .filter(Boolean)
         .map(sku => String(sku).trim())
         .filter(sku => sku !== currentProductSku)
   );
}

function refreshAddOnsCarousel() {
   const $grid = $('.add-ons-grids');
   if (!$grid.length || !$grid.data('flickity')) return;

   $grid.flickity('reloadCells');
   $grid.flickity('resize');
}

function filterAddOnsForSelectedCar() {
   if (shouldHideAddOnsForSelectedCar()) {
      setAddOnsSectionVisible(false);
      return;
   }

   setAddOnsSectionVisible(true);

   const allowedSkus = getAllowedAddOnSkusForSelectedCar();
   if (!allowedSkus) return;

   $('.jsc-add-ons').each(function () {
      const $addOn = $(this);
      const sku = String($addOn.attr('data-sku') || '').trim();
      const shouldShow = allowedSkus.has(sku);

      if (shouldShow) {
         $addOn.show();
         return;
      }

      if ($addOn.hasClass('active')) {
         appendTotalAmountItem($addOn, true);
      }

      $addOn.removeClass('active');
      $addOn.find('.jsc-selected-button').hide();
      $addOn.hide();
   });

   $('.bundle').each(function () {
      const $bundle = $(this);
      const hasVisibleAddOns = $bundle.find('.jsc-add-ons').filter(function () {
         return $(this).css('display') !== 'none';
      }).length > 0;

      $bundle.toggle(hasVisibleAddOns);
      if (!hasVisibleAddOns) {
         $bundle.find('.required').removeClass('required alert');
         $bundle.find('.alert-message, .required-svg').addClass('d-none');
      }
   });

   refreshAddOnsCarousel();
   calculateTotalAmount();
}

addOnsCheck = (exhaustDb) =>{
   setTimeout(() => {
      $('.add-ons-grids').flickity({
         cellAlign: 'left',
         contain: true,
         pageDots: false,
         groupCells: 2
       });
      filterAddOnsForSelectedCar();
   }, 300);
}

document.addEventListener('DOMContentLoaded', function () {
   filterAddOnsForSelectedCar();
   setTimeout(filterAddOnsForSelectedCar, 500);
   setTimeout(filterAddOnsForSelectedCar, 1500);
});

document.addEventListener('shopify:section:load', filterAddOnsForSelectedCar);
document.addEventListener('shopify:section:select', filterAddOnsForSelectedCar);
// Working Select/Unselect
selectAddOns = (e) => {
    var _this = $(e);
    var isActive = _this.hasClass('active');

    // Remove "active" state from all siblings and hide their buttons
    _this.siblings().removeClass('active');
    _this.siblings().find('.jsc-selected-button').hide();

    if (isActive) {
        // If already active, unselect it and remove total text
        _this.removeClass('active');
        _this.find('.jsc-selected-button').hide();
        appendTotalAmountItem(_this, true); // Pass 'true' to remove total text
    } else {
        // Otherwise, select it
        _this.addClass('active');
        _this.find('.jsc-selected-button').show();
        appendTotalAmountItem(_this, false); // Add total text
    }
};
appendTotalAmountItem = (selectedItem, isRemoving = false) => {
   var bundle = selectedItem.parents('.bundle').find('.type-heading').text();
   var required = selectedItem.parents('.bundle').find('.required');
   var bundleNumber = parseInt(selectedItem.parents('.bundle').find('.type-heading').attr('data-count'));
   var title = selectedItem.find('.card__title').text();
   var price = selectedItem.find('.price').find('.price__default .price__current').text();
   var totalItemSelector = `#total-amount .bundle-${bundleNumber}`;

   if (isRemoving) {
      // Remove the total amount item when unselected
      $(totalItemSelector).remove();
   } else {
      // Add or update the total amount item
      if ($(totalItemSelector).length > 0) {
         $(totalItemSelector).html(`<span class="total-text">${title}</span><span class="line-price">${price}</span>`);
      } else {
         var addedItem = `<div class="total-amount-item bundle-${bundleNumber}"><span class="total-text">${title}</span><span class="line-price">${price}</span></div>`;
         $(addedItem).insertBefore('#total-amount .total-amount');
      }
   }

   // Remove required alert if applicable
   if ($(required).length > 0) {
      $(required).removeClass('required alert');
      $(required).find('.alert-message, .required-svg').addClass('d-none');
   }

   calculateTotalAmount();
};

calculateTotalAmount = () => {
   var total = 0;
   var linePrice = 0;

   // Select all total-text elements
   var totalTextElements = $('#total-amount .total-text');

   // Re-index items dynamically to remove gaps in numbering
   totalTextElements.each((index, element) => {
      var lineTitle = $(element).text().replace(/^\d+\.\s*/, ''); // Remove any existing numbering
      var newIndex = index + 1;
      $(element).text(newIndex + ". " + lineTitle);
   });

   $('#total-amount .line-price').each((index, element) => {
      linePrice = $(element).text().trim().replace(/[^\d.]/g, '');
      linePrice = parseFloat(linePrice) || 0; // Ensure valid number
      total += linePrice;
   });

   var formattedAmount = Number(total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
   var formattedShopifyMoney = theme.settings.moneyWithCurrencyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, formattedAmount); 
   $('#total-amount .total-amount-price').text(formattedShopifyMoney);
}

addExhaust = (e, vid) =>{
   var _this = $(e);
   var _activeSelection = $('.jsc-add-ons.active');
   var required = $('.bundle .required');
   var _html = '';
   var _temp = 1;
   var _bindId = vid;
   var itemsArray = [];

   const compatiblePartElement = $('.compatible-part');
   // Get brand, model, and engine details from local storage
   const brand = getLocalStorage('brand');
   const model = getLocalStorage('model');
   const engine = getLocalStorage('engine');
   const engineArray = engine.split('--');

   // Check compatibility of the part with the selected criteria
   const isCompatiblePart = checkPartCompatibility(cachedCarDb, brand, model, engineArray, compatiblePartElement);

   if ($(required).length > 0) {
      $(required).each((index, element) => {
         $(element).addClass('alert');
         $(element).find('.required-svg').removeClass('d-none');
         $(element).find('.alert-message').removeClass('d-none');
      });
      var parent_id = $(required[0]).parent().attr('id');
      if (isCompatiblePart) {
         var errorHtml = `Click <a href="#${parent_id}">here</a> to choose the required bundle.`;
      } else {
         var errorHtml = `The parts are unavailable for your selected vehicle. Please <span class="cursor d-inline-block underline fontWeight-9" onclick="changeVehicleTrigger(this,'page')">select</span> a different vehicle to proceed. If you need assistance, please contact us.`;
      }
      $('.exhaust-error.js-form-error').html(errorHtml);
      $('.exhaust-error.js-form-error').removeAttr('hidden');
   } else {
      $('.exhaust-error.js-form-error').attr('hidden');

      loader.show();

      _activeSelection.each(function(){
         var _active = $(this);
         var _title = _active.attr('data-title').trim();
         var _vid = _active.attr('data-vid');
         var _sku = _active.attr('data-sku');
         var _price = _active.find('.price__default .price__current').text();

         if (_title != 'No Selection') {
            // Determine property name based on keywords in the title
            var propertyKey = "Add_on"; // Default in case no match is found

            if (_title.toLowerCase().includes("connection tube")) {
               propertyKey = "Tube";
            } else if (_title.toLowerCase().includes("intermediate pipe")) {
               propertyKey = "Pipe";
            } else if (_title.toLowerCase().includes("tail pipe")) {
               propertyKey = "Tail Pipe";
            } else if (_title.toLowerCase().includes("tailpipe")) {
               propertyKey = "Tail Pipe";
            } else if (_title.toLowerCase().includes("tailpipes")) {
               propertyKey = "Tail Pipe";
            }

            var _objs = {
               id: _vid,
               quantity: 1,
               properties: {
                  "_bindId": "blank",
                  [propertyKey]: `${_title} x 1 - ${_price}` // Use dynamically assigned label
               }
            };
            itemsArray.push(_objs);
         }

         _html += `<input type="text" name="properties[${propertyKey}]" value="${_title} x 1 - ${_price}" style="display: none;">`;
         _bindId += `-${_vid}`;

         _temp++;
      });

      $.each(itemsArray, function(index, item) {
         item.properties['_bindId'] = _bindId;
      });

      CartJS.addItems(itemsArray);

      _html += `<input type="text" name="properties[_bindId]" value="${_bindId}" style="display: none;"><input type="text" name="properties[_main_product]" value="true" style="display: none;">`;

      $(_html).insertBefore(_this);

      $(document).on('cart.requestComplete', function(event, cart) {
         _this.siblings('.jsc-add-to-cart').click();
         setTimeout(() => {
            loader.hide();
         }, 3000);
      });
   }
}

removeExhaust = (e) =>{
   loader.show();
   var _this = $(e);
   var obj = {};

   // Get the bind ID
   var bindId = _this.attr('data-bindid');
   var selector = $(`.jsc-bindid-${bindId}`);

   selector.each(function(){
      obj[$(this).attr('data-key')] = 0;
   });

   CartJS.updateItemQuantitiesById(obj);

   $(document).on('cart.requestComplete', function(event, cart) {
      // loader.hide();
      setTimeout(() => {
         location.reload();
      }, 3000);
   });
}



/* =========================================================
   16. SWIPERS / SLIDERS
========================================================= */

updatePaginationStyles = (swiper) => {
   const paginationBullets = $(swiper.el).find('.swiper-pagination-bullet');
   const totalSlides = paginationBullets.length;
   const bulletWidth = 100 / totalSlides - 5;

   paginationBullets.each(function() {
      $(this).css('width', `${bulletWidth}%`);
   });
}

updateSlideHeights = (swiper) => {
   const $slides = $(swiper.el).find('.swiper-slide');
   $slides.css('height', 'auto');
   var maxHeight = 0;
   $slides.each(function() {
      var $slide = $(this);
      var slideHeight = $slide.outerHeight();
      if (slideHeight > maxHeight) {
         maxHeight = slideHeight;
      }
   });

  $slides.css('height', maxHeight + 'px');
}

initMultiColumnSwiper = () => {
    $('.swiper.multiColumnSwiper').each(function(index, element) {
        var _this = $(element);
        var autoplay = _this.data('autoplay');
        var autoplaySpeed = _this.data('autoplay-speed');

        var swiper_option = {
            autoHeight: true, 
            slidesPerView: 1,
            spaceBetween: 5,
            on: {
               init: function () {
                  updatePaginationStyles(this);
                  updateSlideHeights(this);
               }, 
               resize: function () {
                  updatePaginationStyles(this);
                  updateSlideHeights(this);
               },
            },
            pagination: {
               el: ".swiper-pagination",
            },
            breakpoints: {
               480: {
                  slidesPerView: 3,
                  spaceBetween: 15,
               },
               768: {
                  slidesPerView: 4,
                  spaceBetween: 20,
               }
            },
            initialSlide : 0
        }

        if (autoplay && autoplaySpeed != '') {
            swiper_option['autoplay'] = {
                delay: autoplaySpeed,
                disableOnInteraction: false
            }
        }
        var swiper = new Swiper(this, swiper_option);
    });
}

const filterPage = document.querySelector('filter-page');

if (filterPage && filterPage.offsetParent !== null) {
  const link = document.querySelector('a[href*="ecu-piggyback-2-euro-cars"].jsc-link-list-ecu-piggyback-2-euro-cars');
  if (link) {
    link.textContent = 'ECU PIGGYBACK (Euro Cars)';
  }
}

//document.querySelector('a[href*="ecu-piggyback-2-euro-cars"].jsc-link-list-ecu-piggyback-2-euro-cars').textContent = 'ECU PIGGYBACK (Euro Cars)'; 
document.addEventListener("DOMContentLoaded", function() {
    var bannerElement = document.querySelector('.banner-model-car');
    if (bannerElement) {
        bannerElement.classList.remove('hidden');
    }
});

$('#home-header-car, .slideshow .btn').on('click', function() {
    const targetElement = $('#banner-model-car'); 
    const offset = 100; 
    const elementPosition = targetElement.offset().top - offset;

    $('html, body').animate({ scrollTop: elementPosition }, 'smooth');
    return false;
});
document.querySelectorAll('.slideshow .btn--secondary').forEach(function(element) {
  element.addEventListener('click', function(event) {
    event.preventDefault();

    const href = element.getAttribute('href');
    if (!href || !href.startsWith('#')) return;

    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    } else {
      console.warn("Target not found for:", href);
    }
  });
});



/* =========================================================
   17. GENERAL CLICK / POPUP HANDLERS
========================================================= */

function closeCarPopup() {
  $('jsc-popup').hide();
  $('#myPopup').hide();
  $('body').removeClass('model-open');
}

document.addEventListener('click', function (event) {
  const isOpen = $('jsc-popup').is(':visible') || $('#myPopup').is(':visible');
  if (!isOpen) return;

  const clickedInsideContent = event.target.closest(
    'jsc-popup-content, jsc-popup-body, jsc-popup-select, jsc-popup-my-model, .jsc-popup-content, .jsc-popup-body, #myPopup > *'
  );

  const clickedOpenTrigger = event.target.closest(
    '.jsc-header-car, .jsc-nav-car, .jsc-mobile-view-car, .jsc-header-car-mobile, #home-header-car, #menu_banner, .btn_cv, .open-car-selector, [onclick*="openCarPopup"]'
  );

  if (clickedInsideContent || clickedOpenTrigger) return;

  closeCarPopup();
});

document.addEventListener('keydown', function (event) {
  if (event.key === 'Escape') closeCarPopup();
});

document.addEventListener("click", function (e) {
  const btn = e.target.closest('.text-overlay__block .btn--primary');
  if (!btn) return;

  // Skip live chat button
  if (btn.id === "livechat") return;

  const href = btn.getAttribute('href');

  // ⚠️ If there is a legit URL (non-empty, not just "#"), let it behave normally
  if (href && href !== "#") {
    return;
  }

  // Only here if href is "", null, or "#"
  e.preventDefault();
  e.stopPropagation();

  // OPEN CAR SELECTOR POPUP
  if (typeof openCarPopup === "function") {
    openCarPopup(e);
  } else if (typeof openCarFilters === "function") {
    openCarFilters();
  }
});

// Open car selector when a link/button has href="#"
// Open car selector only for intended links with href="#"
document.addEventListener("click", function (e) {
  const el = e.target.closest('a[href], .btn[href]');
  if (!el) return;

  // Ignore Shopify dynamic checkout controls
  if (
    el.closest('.shopify-payment-button') ||
    el.closest('shopify-buy-it-now-button') ||
    el.closest('shopify-google-pay-button') ||
    el.closest('more-payment-options-link') ||
    el.classList.contains('shopify-payment-button__more-options')
  ) {
    return;
  }

  // Ignore live chat
  if (
    el.id === "livechat" ||
    el.getAttribute("href") === "#livechat"
  ) {
    return;
  }

  // Ignore header and mega-navigation links
  if (
    el.closest('.main-nav') ||
    el.closest('.mega-nav') ||
    el.classList.contains('main-nav__item-content') ||
    el.classList.contains('main-nav__item--primary')
  ) {
    return;
  }

  const href = (el.getAttribute("href") || "").trim();

  if (href !== "#") return;

  e.preventDefault();
  e.stopPropagation();

  if (typeof openCarPopup === "function") {
    openCarPopup(e);
    return;
  }

  const popup = document.getElementById("myPopup");

  if (popup) {
    popup.style.display = "block";
    document.body.classList.add("model-open");
  }
}, true);

// === Fetch Brand and Exhaust DBs ===
const fetchBrandDb = () => {
  return fetchBrandrDb();
};

(function () {
  const threshold = 500;
  let initialized = false;

  function attach(el) {
    if (initialized) return;
    initialized = true;

    function toggle() {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      el.classList.toggle("is-hidden", y < threshold);
    }

    // Let Respond.io finish mounting
    setTimeout(() => {
      el.classList.add("is-hidden");
      toggle();
    }, 400);

    window.addEventListener("scroll", toggle, { passive: true });
  }

  function init() {
    // Only run on homepage
    if (!document.body.classList.contains("home")) return false;

    const el = document.getElementById("dbee1");
    if (!el) return false;

    attach(el);
    return true;
  }

  // Try immediately
  if (init()) return;

  // Watch for Respond.io injection
  const observer = new MutationObserver(() => {
    if (init()) observer.disconnect();
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  // Safety fallback
  setTimeout(() => {
    observer.disconnect();
    init();
  }, 8000);
})();




/* =========================================================
   18. ARTICLE HASH SCROLLING
========================================================= */

(function () {
  /* ============================
     RUN ONLY ON ARTICLE PAGES
  ============================ */
  const articleRoot = document.querySelector('.page-builder-article');

  // Exit if not article page or element is hidden
  if (
    !articleRoot ||
    articleRoot.offsetParent === null || // not visible
    getComputedStyle(articleRoot).display === 'none'
  ) {
    return;
  }

  /* ============================
     CONFIG
  ============================ */
  const EXTRA_GAP = 20;

  const HEADER_SELECTORS = [
    "#shopify-section-header",
    ".header-wrapper",
    "sticky-header",
    "header",
    ".site-header",
    ".section-header"
  ];

  /* ============================
     HELPERS
  ============================ */
  function getHeaderOffset() {
    for (const sel of HEADER_SELECTORS) {
      const el = document.querySelector(sel);
      if (!el) continue;

      const styles = window.getComputedStyle(el);
      const isSticky =
        styles.position === "sticky" ||
        styles.position === "fixed" ||
        el.classList.contains("sticky") ||
        el.classList.contains("is-sticky");

      if (isSticky) {
        return Math.ceil(el.getBoundingClientRect().height) + EXTRA_GAP;
      }
    }
    return 110;
  }

  function findTarget(hash) {
    if (!hash) return null;

    const raw = decodeURIComponent(hash.replace("#", "")).trim();
    if (!raw) return null;

    return (
      document.getElementById(raw) ||
      document.querySelector(`[name="${CSS.escape(raw)}"]`)
    );
  }

  function scrollToTarget(hash, behavior) {
    const el = findTarget(hash);
    if (!el) return false;

    const offset = getHeaderOffset();
    const y = el.getBoundingClientRect().top + window.pageYOffset - offset;

    window.scrollTo({ top: Math.max(0, y), behavior });

    if (typeof el.focus === "function") {
      el.setAttribute("tabindex", "-1");
      el.focus({ preventScroll: true });
    }

    return true;
  }

  function runHashScroll({ behavior = "smooth" } = {}) {
    const hash = window.location.hash;
    if (!hash) return;

    try {
      if ("scrollRestoration" in history) history.scrollRestoration = "manual";
    } catch (e) {}

    const start = Date.now();
    const maxMs = 3000;
    const tickMs = 60;

    const timer = setInterval(() => {
      const ok = scrollToTarget(hash, behavior);
      if (ok || Date.now() - start > maxMs) clearInterval(timer);
    }, tickMs);
  }

  /* ============================
     PREVENT INITIAL HASH JUMP
  ============================ */
  (function neutralizeInitialJump() {
    const hash = window.location.hash;
    if (!hash) return;

    try {
      history.replaceState(null, "", window.location.pathname + window.location.search);
    } catch (e) {}

    window.scrollTo(0, 0);

    setTimeout(() => {
      try {
        history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search + hash
        );
      } catch (e) {
        window.location.hash = hash;
      }
      runHashScroll({ behavior: "smooth" });
    }, 50);
  })();

  /* ============================
     EVENTS
  ============================ */
  document.addEventListener("DOMContentLoaded", () =>
    setTimeout(() => runHashScroll({ behavior: "smooth" }), 50)
  );

  window.addEventListener("load", () =>
    setTimeout(() => runHashScroll({ behavior: "smooth" }), 50)
  );

  window.addEventListener("hashchange", () => {
    setTimeout(() => runHashScroll({ behavior: "smooth" }), 0);
  });
})();


function repositionMobileSelectedVehicle() {
  const mobileBreakpoint = window.matchMedia('(max-width: 767px)');
  const mainMenu = document.querySelector('nav[aria-label="Primary"] .main-nav');
  const selectedVehicle = mainMenu?.querySelector(
    '.main-menu--left-mob__car-finder'
  );

  if (!mainMenu || !selectedVehicle) return;

  // Remember its original desktop position.
  if (!selectedVehicle._originalNextSibling) {
    selectedVehicle._originalNextSibling = selectedVehicle.nextSibling;
  }

  if (mobileBreakpoint.matches) {
    // Move Selected Vehicle above the first menu item, which is Shop.
    if (mainMenu.firstElementChild !== selectedVehicle) {
      mainMenu.insertBefore(selectedVehicle, mainMenu.firstElementChild);
    }
  } else {
    // Restore its original position on desktop.
    const originalNextSibling = selectedVehicle._originalNextSibling;

    if (
      originalNextSibling &&
      originalNextSibling.parentNode === mainMenu
    ) {
      mainMenu.insertBefore(selectedVehicle, originalNextSibling);
    } else {
      mainMenu.appendChild(selectedVehicle);
    }
  }
}

document.addEventListener('DOMContentLoaded', repositionMobileSelectedVehicle);

window.addEventListener('resize', repositionMobileSelectedVehicle);
